import { FC, useState } from "react";

import {
  Alert,
  Button,
  Divider,
  Form,
  FormInstance,
  Input,
  Modal,
  Select,
  Space,
} from "antd";
import TextArea from "antd/lib/input/TextArea";
import { PlusOutlined } from "@ant-design/icons";

import styles from "./ExaminationForm.module.scss";

import { SampleType } from "@/modules";
import useSampleTypes from "@/hooks/useSampleTypes";
import { DatePicker } from "@/components";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  setExamMode,
  setNotes,
  setOperator,
  setSampleManufacturedDate,
  setSampleManufacturerName,
  setSampleName,
  setSampleSerialNumber,
  setSampleType,
} from "@/store/reducers/examinationForm/examinationFormSlice";

const { Option } = Select;

interface ExaminationFormProps {
  form: FormInstance;
  error: string;
  readonly: boolean;
  onSubmit: (values: any) => void;
}

const ExaminationForm: FC<ExaminationFormProps> = ({
  form,
  error,
  readonly,
  onSubmit,
}) => {
  const dispatch = useAppDispatch();
  const {
    operator,
    notes,
    sampleName,
    sampleManufacturerName,
    sampleManufacturedDate,
    sampleType,
    sampleSerialNumber,
    examMode,
  } = useAppSelector((state) => state.examinationForm);
  const [sampleTypeModal, setSampleTypeModal] = useState(false);

  const {
    sampleTypes,
    loading: sampleLoading,
    addNewSampleType,
    editSampleType,
    deleteSampleType,
  } = useSampleTypes();

  return (
    <>
      <Modal
        title="Sample types"
        width="fit-content"
        visible={sampleTypeModal}
        footer={false}
        onCancel={() => setSampleTypeModal(false)}
      >
        <SampleType
          sampleTypeList={sampleTypes}
          addSampleType={addNewSampleType}
          editSampleType={editSampleType}
          deleteSampleType={deleteSampleType}
        />
      </Modal>
      <Form
        form={form}
        className={styles.form}
        disabled={readonly}
        hideRequiredMark
        name="examination"
        initialValues={{ sampleManufacturedDate: new Date() }}
        labelCol={{ span: 15 }}
        onFinish={onSubmit}
      >
        <Form.Item
          label="Exam mode"
          rules={[
            {
              required: true,
              message: "Please input the exam mode",
            },
          ]}
        >
          <Input
            value={examMode}
            onChange={(e) => dispatch(setExamMode(e.target.value))}
          />
        </Form.Item>

        <Form.Item
          label="Test sample's manufacturer name"
          rules={[
            {
              required: true,
              message:
                "Please input the name of the manufacturer of the test sample",
            },
          ]}
        >
          <Input
            value={sampleManufacturerName}
            onChange={(e) =>
              dispatch(setSampleManufacturerName(e.target.value))
            }
          />
        </Form.Item>
        <Form.Item
          label="Test sample's type"
          rules={[
            {
              required: true,
              message: "Please input the type test sample",
            },
          ]}
        >
          <Select
            loading={sampleLoading}
            placeholder="Select an option"
            allowClear
            value={sampleType}
            onChange={(v) => dispatch(setSampleType(v))}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider className={styles.selectDivider} />
                <Space className={styles.selectButton}>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => setSampleTypeModal(true)}
                  >
                    Add item
                  </Button>
                </Space>
              </>
            )}
          >
            {sampleTypes.map((sampleType) => (
              <Option key={sampleType.sampleTypeId} value={sampleType.type}>
                {sampleType.type}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Test sample's name"
          rules={[
            {
              required: true,
              message: "Please input the name test sample",
            },
          ]}
        >
          <Input
            value={sampleName}
            onChange={(e) => dispatch(setSampleName(e.target.value))}
          />
        </Form.Item>

        <Form.Item
          label="Manufactured date"
          rules={[
            {
              required: true,
              message: "Please input the manufactured date",
            },
          ]}
        >
          <DatePicker
            value={new Date(sampleManufacturedDate)}
            onChange={(value) =>
              value &&
              dispatch(
                setSampleManufacturedDate(value.toJSON().substring(0, 10))
              )
            }
            disabled={readonly}
          />
        </Form.Item>

        <Form.Item
          label="Serial number"
          rules={[
            {
              required: true,
              message: "Please input the serial number",
            },
          ]}
        >
          <Input
            value={sampleSerialNumber}
            onChange={(e) => dispatch(setSampleSerialNumber(e.target.value))}
          />
        </Form.Item>

        <Form.Item
          label="Operator"
          rules={[
            {
              required: true,
              message: "Please input the operator",
            },
          ]}
        >
          <Input
            value={operator}
            onChange={(e) => dispatch(setOperator(e.target.value))}
          />
        </Form.Item>

        <Form.Item label="Notes">
          <TextArea
            value={notes}
            onChange={(e) => dispatch(setNotes(e.target.value))}
            disabled={readonly}
            rows={3}
          />
        </Form.Item>

        {error && (
          <Form.Item wrapperCol={{ span: 20 }}>
            <Alert type="error" description={error} />
          </Form.Item>
        )}
      </Form>
    </>
  );
};

export default ExaminationForm;
